import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Div from '@hellodarwin/core/lib/components/common/div';
import { Cell, Legend, Pie, PieChart as RechartPieChart, } from 'recharts';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import useChartTheme from '../chart-theme';
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = (props, isHighlightingActive) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
    if (isHighlightingActive)
        return null;
    const isSmall = (percent !== null && percent !== void 0 ? percent : 0) < 0.055;
    const centerX = Number(cx !== null && cx !== void 0 ? cx : 0);
    const centerY = Number(cy !== null && cy !== void 0 ? cy : 0);
    const sliceMidAngle = Number(midAngle !== null && midAngle !== void 0 ? midAngle : 0);
    const sliceInnerRadius = Number(innerRadius !== null && innerRadius !== void 0 ? innerRadius : 0);
    const sliceOuterRadius = Number(outerRadius !== null && outerRadius !== void 0 ? outerRadius : 100);
    const radius = sliceOuterRadius - (sliceOuterRadius - sliceInnerRadius) * 0.3;
    const x = centerX + radius * Math.cos(-sliceMidAngle * RADIAN);
    const y = centerY + radius * Math.sin(-sliceMidAngle * RADIAN);
    const fontSize = Math.max(10, Math.min(18, Math.round(sliceOuterRadius * 0.11)));
    if (isSmall)
        return null;
    return (_jsx("text", { x: x, y: y, fill: 'white', textAnchor: 'middle', dominantBaseline: "central", fontSize: fontSize, children: `${((percent !== null && percent !== void 0 ? percent : 1) * 100).toFixed(0)}%` }));
};
const renderLegend = (totalWidth, outerRadius, data, colors, highlightedValue, highlightedValues, labelMap, shortenedLegend, extraPadding) => {
    let legendData = data;
    let legendColors = colors;
    if (highlightedValues && highlightedValues.length > 0) {
        const set = new Set(highlightedValues.map((v) => String(v).trim()).filter(Boolean));
        const keptIdx = [];
        legendData = data.filter((entry, idx) => {
            var _a;
            const key = String((_a = entry.name) !== null && _a !== void 0 ? _a : '').trim();
            const keep = key.length > 0 && set.has(key);
            if (keep)
                keptIdx.push(idx);
            return keep;
        });
        legendColors = keptIdx.map((i) => colors[i]);
    }
    else if (highlightedValue) {
        const highlightedIndex = data.findIndex((entry) => entry.name === highlightedValue);
        if (highlightedIndex !== -1) {
            legendData = [data[highlightedIndex]];
            legendColors = [colors[highlightedIndex]];
        }
        else {
            legendData = [];
            legendColors = [];
        }
    }
    const total = data.reduce((acc, cur) => acc + cur.value, 0);
    const displayedLegendData = shortenedLegend
        ? legendData.slice(0, 4)
        : legendData;
    const legendFontSize = Math.max(10, Math.min(18, Math.round(outerRadius * 0.12)));
    const pad = extraPadding !== null && extraPadding !== void 0 ? extraPadding : 0;
    return (_jsx(Div, { flex: "column", gap: 4, style: { marginLeft: pad }, children: displayedLegendData.map((entry, index) => {
            var _a;
            const percent = total > 0 ? (entry.value / total) * 100 : 0;
            return (_jsxs(Div, { flex: "row", gap: 6, align: "center", children: [_jsx("div", { style: {
                            backgroundColor: legendColors[index % legendColors.length],
                            width: 12,
                            height: 12,
                            borderRadius: 4,
                        } }), _jsxs("span", { style: {
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: legendFontSize,
                            width: Math.max(80, totalWidth - outerRadius - 150 - pad),
                            display: 'inline-block',
                            lineHeight: '1.2',
                        }, children: [labelMap
                                ? !!labelMap[entry.name]
                                    ? labelMap[entry.name]
                                    : entry.name
                                : ((_a = entry.name) !== null && _a !== void 0 ? _a : `Slice ${index + 1}`), ' ', "(", percent.toFixed(0), "%)"] })] }, index));
        }) }));
};
const PieChart = ({ data, width = 300, height = 300, highlightedValue, highlightedValues, labelMap, shortenedLegend, }) => {
    const theme = useTheme();
    const { getColor } = useChartTheme();
    const chartData = useMemo(() => data !== null && data !== void 0 ? data : [], [data]);
    const normalizedHighlightedValues = useMemo(() => {
        var _a;
        return (_a = highlightedValues === null || highlightedValues === void 0 ? void 0 : highlightedValues.map((value) => String(value).trim()).filter((value) => value.length > 0)) !== null && _a !== void 0 ? _a : [];
    }, [highlightedValues]);
    const highlightedValueSet = useMemo(() => normalizedHighlightedValues.length > 0
        ? new Set(normalizedHighlightedValues)
        : undefined, [normalizedHighlightedValues]);
    const highlightedIndex = useMemo(() => highlightedValue
        ? chartData.findIndex((entry) => entry.name === highlightedValue)
        : undefined, [chartData, highlightedValue]);
    const highlightingActive = !!highlightedValue || normalizedHighlightedValues.length > 0;
    const multiCount = normalizedHighlightedValues.length > 1
        ? normalizedHighlightedValues.length
        : undefined;
    const sliceStates = useMemo(() => chartData.map((entry, index) => {
        var _a;
        const isHighlighted = highlightedValueSet
            ? highlightedValueSet.has(String((_a = entry.name) !== null && _a !== void 0 ? _a : '').trim())
            : highlightedValue
                ? entry.name === highlightedValue
                : false;
        return {
            color: getColor(index, highlightedIndex, isHighlighted, multiCount),
            isHighlighted,
        };
    }), [
        chartData,
        getColor,
        highlightedIndex,
        highlightedValue,
        highlightedValueSet,
        multiCount,
    ]);
    const sliceColors = useMemo(() => sliceStates.map((sliceState) => sliceState.color), [sliceStates]);
    if (!chartData.length)
        return null;
    const LEGEND_RIGHT_SPACE = 160;
    const PADDING = 4;
    const availableWidthForPie = Math.max(0, width - (LEGEND_RIGHT_SPACE + PADDING));
    const dynamicOuterRadius = Math.max(40, Math.floor(Math.min(availableWidthForPie, height) / 2 - PADDING));
    const pieCx = Math.max(dynamicOuterRadius + PADDING, Math.floor(availableWidthForPie / 2));
    const pieCy = Math.floor(height / 2);
    return (_jsxs(RechartPieChart, { width: width, height: height, style: {
            cursor: !!highlightedValue ? 'pointer' : 'default',
        }, children: [_jsx("defs", { children: _jsx("filter", { id: "blur", x: "-10%", y: "-10%", width: "120%", height: "120%", children: _jsx("feGaussianBlur", { stdDeviation: "4" }) }) }), _jsx(Pie, { data: chartData, labelLine: false, label: (props) => renderCustomizedLabel(props, highlightingActive), outerRadius: dynamicOuterRadius, cx: pieCx, cy: pieCy, fill: theme.colors.grey_1, dataKey: "value", children: chartData.map((entry, index) => {
                    const { color, isHighlighted } = sliceStates[index];
                    if (highlightingActive && !isHighlighted) {
                        return (_jsx(Cell, { fill: color, opacity: 0.5, filter: "url(#blur)" }, `cell-${entry.name}`));
                    }
                    return (_jsx(Cell, { fill: color, opacity: 1 }, `cell-${entry.name}`));
                }) }), _jsx(Legend, { verticalAlign: "middle", layout: "vertical", align: "right", content: () => renderLegend(width, dynamicOuterRadius, chartData, sliceColors, highlightedValue, highlightedValues, labelMap, shortenedLegend, PADDING) })] }));
};
export default PieChart;
